import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { pageLoaded } from 'src/actions'
import DashboardContainer from 'src/containers/DashboardContainer'
import {
  eventSelector,
  eventModalSelector,
  isRefreshModalVisibleSelector,
  eventTokenSelector,
  invitationTokenSelector,
  eventFormSelector,
  flyerSelector
} from 'src/selectors'
import NoticeYellow from 'src/assets/icons/ico-notice-yellow.png'
import RSVPModalContainer from 'src/components/rsvp_modal/RSVPModalContainer'
import ImageModalContainer from 'src/components/modals/ImageModalContainer'
import ToastMessagesContainer from 'src/components/global/ToastMessagesContainer'
import HobnobModal from 'components/common/HobnobModalV2/HobnobModal'
import Button from 'components/common/Button'
import EventSection from './EventSection/EventSection'
import FlyerContainer from './FlyerContainer/FlyerContainer'
import cn from 'classnames'
import styles from './guest-event.scss'
import NoUrlsAllowedModal from 'src/components/NoUrlsAllowedModal/NoUrlsAllowedModal'
import QRCodeDownloadBanner from 'src/components/common/QRCodeDownloadBanner/QRCodeDownloadBanner'
import { isHobnobAndroidClient, isHobnobIosClient, deepCopy } from 'src/services/utils'
import SelectFlyerStep from 'src/containers/event/CreateEvent/SelectFlyerStep/SelectFlyerStep'
import BackIcon from 'src/assets/icons/back_button.png'
import SvgIcon from 'src/components/common/SvgIcon'
import SearchIcon from 'src/assets/icons/ico-search-outlined.svg'
import { redirectDomain } from 'services/url_helpers'
import { fetchFlyerDesigns, setCustomFlyerURL, clearCustomFlyerURL } from 'src/actions/flyer'
import {
  fetchUploadFlyer,
  resetEventForm,
  fetchUploadVideoFlyer,
  changeRefreshModal,
  changeEventForm,
  fetchZazzleEventInfo
} from 'src/actions/event'
import { message } from 'antd'
import ImageSearchModal from '../../CreateEvent/ImageSearchModal/ImageSearchModal'
import LoadingComponent from 'components/common/LoadingComponent/LoadingComponent'
import moment from 'moment-timezone'
import CustomFlyerPicker from 'src/components/common/CustomFlyerPicker'
import ImagesIcon from 'src/assets/icons/icon_images.png'

const GuestEvent = () => {
  const dispatch = useDispatch()
  const event = useSelector(eventSelector)
  const eventModal = useSelector(eventModalSelector)
  const isRefreshModalVisible = useSelector(isRefreshModalVisibleSelector)
  const [showQRCodeDownloadBanner, setShowQRCodeDownloadBanner] = useState(true)
  const eventToken = useSelector(eventTokenSelector)
  const invitationToken = useSelector(invitationTokenSelector)

  const eventForm = useSelector(eventFormSelector)
  const [currentView, setCurrentView] = useState('detail') // 'detail' or 'flyerSelection'
  const { customFlyerURL } = useSelector(flyerSelector)
  const onClearCustomFlyerURL = () => dispatch(clearCustomFlyerURL())
  const onResetEventForm = () => dispatch(resetEventForm())
  const [isLoading, setIsLoading] = useState(false)
  const [isOpenImageSearch, setIsOpenImageSearch] = useState(false)
  const [isImageSearchLoading, setIsImageSearchLoading] = useState(false)
  const [zazzleFlyerImageUrl, setZazzleFlyerImageUrl] = useState(null)

  useEffect(() => {
    dispatch(fetchFlyerDesigns())
    dispatch(pageLoaded())
    handleClearAndResetEventForm()
  }, [])

  const renderDraftNotice = () => {
    return (
      <div className={styles['draft-notice']}>
        <img src={NoticeYellow} width="20" />
        <span>
          This is a draft preview, activate it to send your invite! (you can edit it anytime)
        </span>
      </div>
    )
  }

  const handleRetryFlyerUpload = (flyer, designNumber) => {
    const deepCopyEvent = deepCopy(event)

    deepCopyEvent.starts_at = event.starts_at
      ? moment.tz(event.starts_at, event.timezone).format('YYYY-MM-DD HH:mm')
      : event.starts_at
    deepCopyEvent.ends_at = event.ends_at
      ? moment.tz(event.ends_at, event.timezone).format('YYYY-MM-DD HH:mm')
      : event.ends_at

    deepCopyEvent.flyer_design_number = String(flyer.design_number)
    deepCopyEvent.flyer_design_slug = flyer.design_slug

    dispatch(changeEventForm(deepCopyEvent))

    if (designNumber === 0 && event?.partner_data?.design_id) {
      if (event?.partner_data?.design_image_url) {
        setZazzleFlyerImageUrl(event?.partner_data?.design_image_url)
        setCurrentView('flyerSelection')
      } else {
        dispatch(
          fetchZazzleEventInfo({
            design_id: event?.partner_data?.design_id,
            refresh: true,
            onSuccessCallback: data => {
              if (data?.design_file_url?.Front) {
                setZazzleFlyerImageUrl(data?.design_file_url?.Front)
                setCurrentView('flyerSelection')
              }
            },
            onErrorCallback: () => {
              setCurrentView('flyerSelection')
            }
          })
        )
      }
    } else {
      setCurrentView('flyerSelection')
    }
  }

  const handleChangeFlyer = useCallback((imageFile, mediaType) => {
    const id = event.id
    const url = event.url
    const designId = event?.partner_data?.design_id
    setIsLoading(true)

    if (designId && eventForm.flyer_design_number === '0') {
      handleUploadFlyer(id, url, mediaType, imageFile)
    } else if (mediaType === 'video') {
      if (customFlyerURL) {
        handleUploadFlyer(id, url, mediaType, imageFile)
      } else {
        handleUploadVideoFlyer(id, url, mediaType, imageFile)
      }
    } else {
      handleUploadFlyer(id, url, mediaType, imageFile)
    }
  }, [eventForm.flyer_design_number])
  const handleBackToDetail = () => {
    setCurrentView('detail')
  }

  const handleClearAndResetEventForm = () => {
    onClearCustomFlyerURL()
    onResetEventForm()
  }

  const handleUploadVideoFlyer = (eventId, eventUrl, type, imageFile) => {
    dispatch(
      fetchUploadVideoFlyer({
        eventId,
        design_number: eventForm.flyer_design_number,
        imageBgFile: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.video_flyer?.image_bg_upload?.presigned_url) {
            requests.push(
              fetch(data.video_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: imageFile || eventForm.checkedFlyer
              })
            )
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              handleClearAndResetEventForm()
              redirectDomain(eventUrl + '/')
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        },
        onErrorCallback: () => {
          setIsLoading(false)
          message.error('Creation failed.')
        }
      })
    )
  }

  const handleUploadFlyer = (eventId, eventUrl, type, imageFile) => {
    if (type === 'zazzle') {
      dispatch(
        fetchUploadFlyer({
          eventId,
          design_number: 0,
          customBgDataURL: zazzleFlyerImageUrl,
          onSuccessCallback: async data => {
            const requests = []
            if (data?.image_flyer?.image_upload?.presigned_url) {
              requests.push(
                fetch(data.image_flyer.image_upload.presigned_url, {
                  headers: {
                    'Content-Type': 'image/png'
                  },
                  method: 'PUT',
                  body: imageFile || eventForm.checkedFlyer
                })
              )
            }

            if (data?.image_flyer?.image_bg_upload?.presigned_url) {
              requests.push(
                fetch(data.image_flyer.image_bg_upload.presigned_url, {
                  headers: {
                    'Content-Type': 'image/png'
                  },
                  method: 'PUT',
                  body: imageFile || eventForm.checkedFlyer
                })
              )
            }

            if (requests.length === 0) {
              setIsLoading(false)
              alert('Upload Error.')
              return
            }

            Promise.all(requests).then(responses => {
              if (responses.every(response => response.status === 200)) {
                redirectDomain(eventUrl + '/')
                dispatch(resetEventForm())
              } else {
                setIsLoading(false)
                alert('Upload Error.')
              }
            })
          }
        })
      )
      return
    }

    dispatch(
      fetchUploadFlyer({
        eventId,
        design_number: eventForm.flyer_design_number,
        customBgDataURL: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.image_flyer?.image_upload?.presigned_url) {
            requests.push(
              fetch(data.image_flyer.image_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: imageFile || eventForm.checkedFlyer
              })
            )
          }

          if (data?.image_flyer?.image_bg_upload?.presigned_url) {
            const flyerBgImage = await fetch(customFlyerURL)
            const flyerBgImageBlob = await flyerBgImage.blob()
            requests.push(
              fetch(data.image_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: flyerBgImageBlob
              })
            )
          }

          if (requests.length === 0) {
            setIsLoading(false)
            alert('Upload Error.')
            return
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              handleClearAndResetEventForm()
              redirectDomain(eventUrl + '/')
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        },
        onErrorCallback: () => setIsLoading(false)
      })
    )
  }

  const handleImageSearchSave = async (url) => {
    setIsImageSearchLoading(true)

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      })

      if (response.ok) {
        const blob = await response.blob()
        const reader = new FileReader()

        reader.onload = (e) => {
          dispatch(setCustomFlyerURL(e.target.result))
          setIsOpenImageSearch(false)
          setIsImageSearchLoading(false)
        }

        reader.onerror = () => {
          throw new Error('Failed to read blob')
        }

        reader.readAsDataURL(blob)
        return
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (fetchError) {
      console.warn('Fetch failed, trying Image method:', fetchError)

      try {
        await loadImageWithCanvas(url)
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      } catch (imageError) {
        console.error('Both methods failed:', imageError)
        message.error('Failed to load image. Please try a different image.')
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      }
    }
  }

  const loadImageWithCanvas = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          canvas.width = img.naturalWidth
          canvas.height = img.naturalHeight
          ctx.drawImage(img, 0, 0)

          const dataURL = canvas.toDataURL('image/jpeg', 0.8)
          dispatch(setCustomFlyerURL(dataURL))
          resolve(dataURL)
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        reject(new Error('Image load failed'))
      }

      img.src = url
    })
  }

  const renderHeaderLeft = () => {
    if (currentView === 'flyerSelection') {
      return (
        <>
          <img src={BackIcon} onClick={handleBackToDetail} width="12" />
        </>
      )
    }
    return null
  }

  const renderRightButtons = () => {
    if (currentView === 'flyerSelection') {
      return (
        <section className={styles['action-container']}>
          <div className={styles['action-item']}>
            <CustomFlyerPicker>
              <img src={ImagesIcon} width={24} alt="Select flyer image" />
            </CustomFlyerPicker>

            <SvgIcon
              className={styles['search-icon']}
              icon={SearchIcon}
              onClick={() => setIsOpenImageSearch(true)}
              width={24}
            />
          </div>
        </section>
      )
    }
  }

  const isDraft = event.state === 'draft'

  const showBanner = !isHobnobAndroidClient() && !isHobnobIosClient() && !isDraft && showQRCodeDownloadBanner && eventModal !== 'guests'

  return (
    <DashboardContainer
      menuIcon
      visibleHeaderBar={eventModal === 'details'}
      documentTitle={event.name}
      left={renderHeaderLeft()}
      right={renderRightButtons()}
      width={88}
    >

      {isDraft && renderDraftNotice()}
      {
        showBanner && (
          <QRCodeDownloadBanner
            title="Download the Hobnob App for more exciting features!"
            showCloseIcon
            onClose={() => setShowQRCodeDownloadBanner(false)}
            eventToken={eventToken}
            invitationToken={invitationToken}
          />
        )
      }

      {
        currentView === 'detail' ? (
          <div
            className={cn(styles['guest-event-container'], {
              [styles['guest-event-container-draft']]: isDraft,
              [styles['guest-event-container-banner']]: showBanner
            })}
          >
            <FlyerContainer onRetryFlyerUpload={handleRetryFlyerUpload} />
            <div className={styles['information']}>
              <EventSection onRetryFlyerUpload={handleRetryFlyerUpload} />
              <RSVPModalContainer />
              <ImageModalContainer />
              <ToastMessagesContainer />
            </div>
          </div>

        ) : (
          <div className={styles['flyer-selection-container']}>
            <div className={cn(styles['back-button'])} onClick={handleBackToDetail}>
              <img src={BackIcon} width="12" alt="Go back" />
            </div>

            <div className={styles['create-wrapper']}>
              <header className={styles['create-header-wrapper']}>
                <h1>Design</h1>
                <div
                  className={styles['image-search-label-wrapper']}
                  onClick={() => setIsOpenImageSearch(true)}
                >
                  <SvgIcon className={styles['search-icon']} icon={SearchIcon} />
                  <span>Image Search</span>
                </div>
              </header>
              <div className={styles['create-step-wrapper']}>
                <SelectFlyerStep zazzleFlyerImage={zazzleFlyerImageUrl} onButtonClick={handleChangeFlyer} onClickBack={handleBackToDetail} type="retryUploadFlyer" />
              </div>
            </div>
          </div>
        )
      }

      <HobnobModal
        visible={isRefreshModalVisible}
        onCancel={() => dispatch(changeRefreshModal(false))}
      >
        <div className={styles['refresh-container']}>
          <div className={styles['title']}>
            Your Host has changed the settings of this event. Please refresh the page to RSVP.
          </div>
          <Button className={styles['button-inner']} onClick={() => window.location.reload()}>
            REFRESH
          </Button>
        </div>
      </HobnobModal>

      {
        isOpenImageSearch && currentView === 'flyerSelection' && (
          <ImageSearchModal
            eventName={eventForm.name}
            show={isOpenImageSearch}
            handleCancel={() => setIsOpenImageSearch(false)}
            handleImageSearchSave={handleImageSearchSave}
          />
        )
      }

      <NoUrlsAllowedModal />

      <LoadingComponent loading={isLoading || isImageSearchLoading} dark />
    </DashboardContainer >
  )
}

export default GuestEvent
